import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, BellIcon, MapIcon, CheckIcon } from 'lucide-react';
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: ComponentType<{
    className?: string;
  }>;
}
interface MobileHeaderProps {
  rooms: Room[];
  selectedRoomId: string;
  onSelectRoom: (id: string) => void;
  onOpenMap: () => void;
  onOpenAlerts: () => void;
  hasAlerts?: boolean;
}
export function MobileHeader({
  rooms,
  selectedRoomId,
  onSelectRoom,
  onOpenMap,
  onOpenAlerts,
  hasAlerts = false
}: MobileHeaderProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentRoom = rooms.find(r => r.id === selectedRoomId) || rooms[0];
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);
  const handleSelectRoom = (id: string) => {
    onSelectRoom(id);
    setIsDropdownOpen(false);
  };
  return <div className="relative z-30" data-id="element-310">
      {/* Header Bar */}
      <div className="h-14 flex items-center justify-between px-4 bg-[#0a0f1a]/95 backdrop-blur-md border-b border-white/5" data-id="element-311">
        {/* Left: Zone Dropdown */}
        <div ref={dropdownRef} className="relative" data-id="element-312">
          <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 active:bg-white/10 transition-colors min-w-0" data-id="element-313">
            {(() => {
            const RoomIcon = currentRoom.icon;
            return <RoomIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" data-id="element-314" />;
          })()}
            <span className="text-xs text-slate-300 font-medium truncate max-w-[80px]" data-id="element-315">
              {currentRoom.name}
            </span>
            <ChevronDownIcon className={`w-3.5 h-3.5 text-slate-500 flex-shrink-0 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-316" />
          </button>

          {/* Dropdown */}
          <AnimatePresence data-id="element-317">
            {isDropdownOpen && <motion.div initial={{
            opacity: 0,
            y: -8,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -8,
            scale: 0.95
          }} transition={{
            duration: 0.15
          }} className="absolute top-full left-0 mt-1.5 w-48 py-1.5 rounded-xl bg-slate-800/95 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden max-h-[60vh] overflow-y-auto" data-id="element-318">
                {rooms.map(room => {
              const isSelected = room.id === selectedRoomId;
              const RoomIcon = room.icon;
              return <button key={room.id} onClick={() => handleSelectRoom(room.id)} className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 transition-colors ${isSelected ? 'bg-cyan-500/15 text-cyan-300' : 'text-slate-300 active:bg-white/5'}`} data-id="element-319">
                      <RoomIcon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-cyan-400' : 'text-slate-500'}`} data-id="element-320" />
                      <span className="text-sm font-medium flex-1 text-left" data-id="element-321">
                        {room.name}
                      </span>
                      {isSelected && <CheckIcon className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" data-id="element-322" />}
                    </button>;
            })}
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Center: Zone Name */}
        <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none" data-id="element-323">
          <h1 className="text-base font-bold text-slate-200 tracking-wide" data-id="element-324">
            {currentRoom.name}
          </h1>
        </div>

        {/* Right: Bell + Map */}
        <div className="flex items-center gap-1" data-id="element-325">
          <button onClick={onOpenAlerts} className="relative p-2.5 rounded-lg active:bg-white/5 transition-colors" aria-label="알림" data-id="element-326">
            <BellIcon className="w-5 h-5 text-slate-400" data-id="element-327" />
            {hasAlerts && <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full shadow-[0_0_6px_rgba(239,68,68,0.8)]" data-id="element-328" />}
          </button>
          <button onClick={onOpenMap} className="p-2.5 rounded-lg active:bg-white/5 transition-colors" aria-label="평면도" data-id="element-329">
            <MapIcon className="w-5 h-5 text-slate-400" data-id="element-330" />
          </button>
        </div>
      </div>
    </div>;
}